<template>
  <main class="page-operative page-operative-balance-income-statement d-flex flex-column h-100">
    <div class="flex-grow-1 container-fluid d-flex flex-column h-100">
      <div class="d-flex align-items-center justify-content-between flex-wrap flex-md-nowrap mb-5">
        <PageTitle class="mb-2 mb-md-0">Analisi reddituale</PageTitle>

        <div class="actions">
          <button class="btn btn-primary d-inline mr-2" @click.prevent="toggleShow">
            <template v-if="show === 'month'">Visualizza per annualità</template>
            <template v-else>Visualizza per mensilità</template>
          </button>
          <b-dropdown id="download-dropdown" class="d-inline" :no-caret="true" variant="outline-primary">
            <template v-slot:button-content>
              <i class="fas fa-ellipsis-v mr-2"></i>
              Stampa
            </template>
            <b-dropdown-item @click="printIncomeStatement">Stampa analisi reddituale</b-dropdown-item>
            <b-dropdown-item @click="printBusinessPlan">Stampa business plan</b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div class="flex-grow-1 d-flex flex-column h-100 overflow-auto">
        <LoadingDataLoader v-if="isLoadingData"></LoadingDataLoader>
        <ErrorOnLoadingDataFeedback v-else-if="errorLoadingData"></ErrorOnLoadingDataFeedback>
        <template v-else-if="show === 'month'">
          <div class="overflow-auto report-texts-size">
            <OperativeTable dense sticky>
              <tbody>
              <OperativeTableBodyHeader :years="monthsTableHeadings" show-years></OperativeTableBodyHeader>
              <OperativeTableBodyRow :values="monthsTableRows.ricavi">
                Ricavi
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.deltaScorte">
                ∆ Scorte
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.cogs">
                Cogs
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.grossMargin" strong>
                Gross Margin
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.marginePerc" em>
                Margine %
                <template #cell-content="{ value }">{{ value }}%</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow v-if="isModuleProjectsEnabled" :values="monthsTableRows.personale">
                Personale indiretto
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow v-else :values="monthsTableRows.personale">
                Personale
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.gAndA">
                G&amp;A
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.EBITDA" strong>
                EBITDA
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.EBITDAvendite" em>
                EBITDA/Vendite
                <template #cell-content="{ value }">{{ value }}%</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.ammortamenti">
                Ammortamenti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.EBIT" strong>
                EBIT
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.costiFinanziari">
                Costi finanziari
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.EBT" strong>
                EBT
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow v-if="isModuleSubsidizedFinanceEnabled" :values="monthsTableRows.contributiFondoPerduto">
                Contributi a fondo perduto
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.creditoImposta">
                Credito d'imposta
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.tasse">
                Tasse
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.risultatoEconomico" strong>
                Risultato Economico
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.caricoFiscale" em>
                Carico fiscale
                <template #cell-content="{ value }">{{ value }}%</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.breakEven" em>
                Break even
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              </tbody>
            </OperativeTable>
          </div>
        </template>
        <template v-else>
          <AnnualIncomeStatementTable
            class="report-texts-size"
            :isModuleSubsidizedFinanceEnabled="isModuleSubsidizedFinanceEnabled"
            :isModuleProjectsEnabled="isModuleProjectsEnabled"
            :yearsTableHeadings="yearsTableHeadings"
            :yearsTableRows="yearsTableRows"
          />
        </template>
      </div>
    </div>
  </main>
</template>

<script>

import operativePageMixin from '@/mixins/operativePage.js';
import loadingDataMixin from '@/mixins/loadingData.js';
import computedFinancialDataMixin from '@/mixins/computedFinancialData.js';
import formatMoneyFilter from '@/filters/formatMoney.js';
import { companyHasModule } from '@/helpers/modules.js';

import { BDropdown, BDropdownItem } from 'bootstrap-vue';

export default {
  mixins: [operativePageMixin, loadingDataMixin, computedFinancialDataMixin],
  components: {
    PageTitle: () => import('@/views/components/Layout/LayoutOperative/PageTitle.vue'),
    ErrorOnLoadingDataFeedback: () => import('@/views/components/Operative/ErrorOnLoadingDataFeedback.vue'),
    LoadingDataLoader: () => import('@/views/components/Operative/LoadingDataLoader.vue'),
    OperativeTable: () => import('@/views/components/Operative/OperativeTable.vue'),
    OperativeTableBodyHeader: () => import('@/views/components/Operative/OperativeTableBodyHeader.vue'),
    OperativeTableBodyRow: () => import('@/views/components/Operative/OperativeTableBodyRow.vue'),
    AnnualIncomeStatementTable: () => import('@/views/components/Operative/OperativeTable/AnnualIncomeStatementTable.vue'),
    BDropdown,
    BDropdownItem,
  },
  filters: {
    money: formatMoneyFilter,
  },
  data () {
    return {
      show: 'year',
      years: [],
      months: [],
    };
  },
  computed: {
    isModuleSubsidizedFinanceEnabled () {
      return companyHasModule(this.company, 'SUBSIDIZED_FINANCE');
    },
    isModuleProjectsEnabled () {
      return companyHasModule(this.company, 'PROJECTS');
    },
    monthsTableHeadings () {
      return this.convertToMonthsHeading(this.months);
    },
    monthsTableRows () {
      return this.convertToRowGroupAutomatically(this.months);
    },
    yearsTableHeadings () {
      return this.convertToYearsHeading(this.years);
    },
    yearsTableRows () {
      return this.convertToRowGroupAutomatically(this.years);
    },
  },
  methods: {
    retrieveData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();

      if (this.show === 'month') {
        this.$api.getComputedAnalisiRedditualeMensile(this.company?.id)
          .then(res => {
            this.months = res?.data?.months || [];
          })
          .catch(this.setErrorOnLoadingData)
          .finally(this.setDataLoaded)
        ;

        return;
      }

      this.$api.getComputedAnalisiReddituale(this.company?.id)
        .then(res => {
          this.years = res?.data?.years || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    toggleShow () {
      if (this.show === 'month') {
        this.show = 'year';
      } else {
        this.show = 'month';
      }

      this.retrieveData();
    },
    printIncomeStatement () {
      this.$router.push({
        name: 'operative-company.balance.print',
        params: {
          section: 'incomeStatement',
        },
        query: {
          forecast: this.$route.query?.forecast || null,
        },
      });
    },
    printBusinessPlan () {
      this.$router.push({
        name: 'operative-company.balance.print',
        params: {
          section: 'businessPlan',
        },
        query: {
          forecast: this.$route.query?.forecast || null,
        },
      });
    },
  },
  watch: {
    company () {
      this.retrieveData();
    },
  },
};

</script>
